import { JobsitePrinterOption } from 'containers/utils';
import { JobsiteBadgeTemplateOption } from 'containers/worker/helpers/utils';
import React from 'react';
import { useCreateAndPrintTempBadgeMutation } from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { getGraphQLError, hasGraphQLErrorMessage, MatchBy } from 'utils/error';
import { useIsMounted } from 'utils';
import { RequiredField } from 'utils/validation';
import { PrintIcon } from 'components/icons';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { TempBadge } from 'containers/jobsite/tabs/jobsiteTempBadges/types';
import { CreateTempBadgeFormData } from './types';
import {
  getInput,
  getFormDefaultValues,
  getFormInputs,
} from './CreateTempBadgeModalContent.forms';

export interface CreateTempBadgeModalContentProps {
  closeModal: () => void;
  onAction: (
    tempBadge: TempBadge,
    jobsitePrinterOption: JobsitePrinterOption,
  ) => void;
  jobsiteId: string;
  jobsiteName: string;
  defaultJobsitePrinterOption: JobsitePrinterOption | null;
  jobsitePrinterOptions: JobsitePrinterOption[];
  defaultTempBadgeTemplateOption: JobsiteBadgeTemplateOption | null;
  tempBadgeTemplateOptions: JobsiteBadgeTemplateOption[];
}

const instructions = `Design and print a temporary badge. Next you'll register it so it's ready to assign.`;

export function CreateTempBadgeModalContent(
  props: CreateTempBadgeModalContentProps,
): React.ReactElement {
  const {
    jobsiteId,
    jobsiteName,
    closeModal,
    onAction,
    defaultJobsitePrinterOption,
    jobsitePrinterOptions,
    tempBadgeTemplateOptions,
    defaultTempBadgeTemplateOption,
  } = props;
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const isMounted = useIsMounted();

  const [createAndPrintTempBadge] = useCreateAndPrintTempBadgeMutation();

  const onSubmit: FormOnSubmit<CreateTempBadgeFormData> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    try {
      const input = await getInput(jobsiteId, data, dirtyFields);
      const { data: result } = await createAndPrintTempBadge({
        variables: { input },
      });
      const newTempBadge = result.createAndPrintTempBadge.tempBadge;
      onAction(newTempBadge, data.jobsitePrinter);
    } catch (error) {
      if (
        hasGraphQLErrorMessage(
          error,
          'Cannot crop badge image',
          MatchBy.startOfMessage,
        )
      ) {
        formApi.setError('badgeImageFile', {
          message: RequiredField`Photo`,
          shouldFocus: true,
        });
      } else {
        AlertService.alert(
          'danger',
          'Error sending badge print request',
          getGraphQLError(error),
        );
      }
    } finally {
      if (isMounted()) setIsFetching(false);
    }
  };

  const { formInputs, defaultValues } = React.useMemo(() => {
    return {
      formInputs: getFormInputs({
        jobsiteName,
        printerOptions: jobsitePrinterOptions,
        badgeTemplateOptions: tempBadgeTemplateOptions,
      }),
      defaultValues: getFormDefaultValues({
        defaultPrinterOption: defaultJobsitePrinterOption,
        defaultTempBadgeTemplateOption,
      }),
    };
  }, [
    jobsiteName,
    jobsitePrinterOptions,
    defaultJobsitePrinterOption,
    tempBadgeTemplateOptions,
  ]);

  return (
    <ModalFormContent
      setOpen={closeModal}
      renderAbove={<p className="odin-mb-4">{instructions}</p>}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Print Badge"
      actionIcon={PrintIcon}
      actionButtonWithSpinner={isFetching}
    />
  );
}
